import * as d3 from 'd3';
import { calculateBandwidth } from './statistics';
import { gaussianKernel, kernelDensityEstimator } from '../../utils/kernelDensityEstimation';
import { GroupHistogramData } from '../../types/histogram.types';

export interface DensityEstimationData {
  densityData: [number, number][];
  groupDensityData: { groupName: string; densityData: [number, number][] }[];
  densityScale: number;
  maxDensity: number;
  originalXDomain: [number, number];
}

/**
 * カーネル密度推定を計算する（純粋関数）
 */
export function calculateKernelDensityEstimation(options: {
  showDensity: boolean;
  allData: number[];
  densityBandwidth?: number;
  xScale: d3.ScaleLinear<number, number>;
  yScale: d3.ScaleLinear<number, number>;
  histData?: d3.Bin<number, number>[];
  groupHistData?: GroupHistogramData[];
  isGrouped: boolean;
  groupMode: 'overlay' | 'stacked' | 'faceted';
  orientation: 'vertical' | 'horizontal';
  bins: number;
}): DensityEstimationData | null {
  const {
    showDensity, allData, densityBandwidth, xScale, yScale,
    histData, groupHistData, isGrouped, groupMode, orientation, bins
  } = options;

  if (!showDensity || !allData || allData.length === 0) {
    return null;
  }

  if (!xScale || !yScale) {
    return null;
  }

  let densityData: [number, number][] = [];
  let groupDensityData: { groupName: string; densityData: [number, number][] }[] = [];
  let densityScale = 1;
  let maxDensity = 0;

  const originalXDomain = d3.extent(allData) as [number, number];

  if (isGrouped && groupHistData) {
    // グループ化モードの密度推定（overlayモードのみ）
    if (groupMode === 'overlay') {
      groupDensityData = groupHistData.map(group => {
        const groupBandwidth = densityBandwidth || calculateBandwidth(group.originalData.map(d => d.value));
        const kde = kernelDensityEstimator(gaussianKernel, group.originalData.map(d => d.value), groupBandwidth);

        const valueDomain = orientation === 'horizontal' ? yScale.domain() : xScale.domain();
        const xValues = d3.range(
          valueDomain[0],
          valueDomain[1],
          (valueDomain[1] - valueDomain[0]) / 100
        );
        const groupDensity = kde(xValues);

        const groupMax = d3.max(groupDensity, d => d[1]) || 0;
        maxDensity = Math.max(maxDensity, groupMax);

        return {
          groupName: group.groupName,
          densityData: groupDensity
        };
      });

      const binWidth = (originalXDomain[1] - originalXDomain[0]) / bins;
      groupDensityData.forEach((_, index) => {
        const groupData = groupHistData[index];
        const groupScale = groupData.originalData.length * binWidth;
        densityScale = Math.max(densityScale, groupScale);
      });
    }
  } else if (!isGrouped && histData) {
    // 非グループ化モードの密度推定
    const bandwidth = densityBandwidth || calculateBandwidth(allData);
    const kde = kernelDensityEstimator(gaussianKernel, allData, bandwidth);
    const valueDomain = orientation === 'horizontal' ? yScale.domain() : xScale.domain();
    const xValues = d3.range(
      valueDomain[0],
      valueDomain[1],
      (valueDomain[1] - valueDomain[0]) / 100
    );
    densityData = kde(xValues);

    const binWidth = (originalXDomain[1] - originalXDomain[0]) / bins;
    densityScale = allData.length * binWidth;
    maxDensity = d3.max(densityData, d => d[1]) || 0;

    // スケールの範囲を更新（密度も考慮）
    const yMax = Math.max(histData ? (d3.max(histData, d => d.length) || 0) : 0, maxDensity * densityScale);
    if (orientation === 'horizontal') {
      xScale.domain([0, yMax]);
    } else {
      yScale.domain([0, yMax]);
    }
  }

  return { densityData, groupDensityData, densityScale, maxDensity, originalXDomain };
}
